import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  GET_AUTHENTICATION_ATTEMPTS_QUERY,
  GET_SESSIONS_CONFIGURATION_QUERY,
  UPDATE_SESSIONS_CONFIGURATION_MUTATION
} from '../../graphql/iamSessions'
import { getTranslate } from '../../localization'
import {
  AuthenticationAttemptOutcome,
  AuthenticationAttemptReason,
  AuthenticationAttemptsInput,
  AuthenticationAttemptsResponse,
  AuthenticationAttemptStage,
  SessionConfiguration
} from '../../types/iamSessions'
import useStyles from './index.styles'

type FilterForm = {
  identifier: string
  outcome: '' | AuthenticationAttemptOutcome
  reason: '' | AuthenticationAttemptReason
  stage: '' | AuthenticationAttemptStage
  loginSource: string
  from: string
  to: string
}

const emptyFilters: FilterForm = {
  identifier: '',
  outcome: '',
  reason: '',
  stage: '',
  loginSource: '',
  from: '',
  to: ''
}

const outcomes: AuthenticationAttemptOutcome[] = [
  'Success',
  'Failure',
  'Challenge'
]
const stages: AuthenticationAttemptStage[] = [
  'Password',
  'IPPolicy',
  'TwoFactor',
  'Gateway'
]
const reasons: AuthenticationAttemptReason[] = [
  'Authenticated',
  'GatewayCredentialsVerified',
  'TwoFactorRequired',
  'UserNotFound',
  'InvalidCredentials',
  'UserInactive',
  'AccountLocked',
  'IPAccessDenied',
  'TwoFactorInvalid',
  'TwoFactorExpired',
  'TwoFactorAttemptsExceeded'
]

const readable = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace('IP', 'IP ')
    .replace(/\s+/g, ' ')
    .trim()

export default function AdminConfigAuthenticationHistoryTab() {
  const classes = useStyles()
  const [draftFilters, setDraftFilters] = useState(emptyFilters)
  const [filters, setFilters] = useState(emptyFilters)
  const [pageToken, setPageToken] = useState('')
  const [previousTokens, setPreviousTokens] = useState<string[]>([])
  const [retentionDays, setRetentionDays] = useState(365)

  const input = useMemo<AuthenticationAttemptsInput>(() => {
    const result: AuthenticationAttemptsInput = { pageSize: 20 }
    if (filters.identifier.trim()) result.identifier = filters.identifier.trim()
    if (filters.outcome) result.outcome = filters.outcome
    if (filters.reason) result.reason = filters.reason
    if (filters.stage) result.stage = filters.stage
    if (filters.loginSource.trim())
      result.loginSource = filters.loginSource.trim()
    if (filters.from && filters.to) {
      result.occurredRange = {
        from: new Date(filters.from).toISOString(),
        to: new Date(filters.to).toISOString()
      }
    }
    if (pageToken) result.pageToken = pageToken
    return result
  }, [filters, pageToken])

  const { data, loading, error, refetch } = useQuery<{
    authenticationAttempts: AuthenticationAttemptsResponse
  }>(GET_AUTHENTICATION_ATTEMPTS_QUERY, {
    variables: { input },
    fetchPolicy: 'network-only',
    onError: () =>
      toast.error(getTranslate('Unable to load authentication history'))
  })

  const { data: configurationData, refetch: refetchConfiguration } = useQuery<{
    sessionConfiguration: SessionConfiguration
  }>(GET_SESSIONS_CONFIGURATION_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: ({ sessionConfiguration }) =>
      setRetentionDays(
        sessionConfiguration.authenticationHistoryRetentionDays || 365
      ),
    onError: () =>
      toast.error(getTranslate('Unable to load authentication settings'))
  })

  const [updateConfiguration, { loading: savingRetention }] = useMutation(
    UPDATE_SESSIONS_CONFIGURATION_MUTATION,
    {
      onCompleted: () => {
        toast.success(getTranslate('Authentication history retention updated'))
        refetchConfiguration()
      },
      onError: () =>
        toast.error(getTranslate('Unable to update authentication settings'))
    }
  )

  const applyFilters = () => {
    if (
      (draftFilters.from && !draftFilters.to) ||
      (!draftFilters.from && draftFilters.to)
    ) {
      toast.error(getTranslate('Select both start and end date'))
      return
    }
    setFilters(draftFilters)
    setPageToken('')
    setPreviousTokens([])
  }

  const resetFilters = () => {
    setDraftFilters(emptyFilters)
    setFilters(emptyFilters)
    setPageToken('')
    setPreviousTokens([])
  }

  const saveRetention = () => {
    if (!configurationData || retentionDays < 1 || retentionDays > 3650) return
    updateConfiguration({
      variables: {
        input: {
          ...configurationData.sessionConfiguration,
          authenticationHistoryRetentionDays: retentionDays
        }
      }
    })
  }

  const attempts = data?.authenticationAttempts.attempts || []
  const nextPageToken = data?.authenticationAttempts.nextPageToken

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <div className={classes.heading}>
          <Typography variant="h4">
            {getTranslate('Authentication History')}
          </Typography>
          <Typography variant="h5" className={classes.subtitle}>
            {getTranslate(
              'Review successful, failed and challenged authentication attempts across password, IP policy, gateway and 2FA flows.'
            )}
          </Typography>
        </div>
        <div className={classes.filters}>
          <InputWithLabel
            label={getTranslate('Email or identifier')}
            value={draftFilters.identifier}
            onChange={(event) =>
              setDraftFilters({
                ...draftFilters,
                identifier: event.currentTarget.value
              })
            }
          />
          <SelectWithLabel
            label={getTranslate('Outcome')}
            value={draftFilters.outcome}
            onChange={(event: { target: { value: unknown } }) =>
              setDraftFilters({
                ...draftFilters,
                outcome: event.target.value as FilterForm['outcome']
              })
            }>
            <MenuItem value="">{getTranslate('All outcomes')}</MenuItem>
            {outcomes.map((outcome) => (
              <MenuItem key={outcome} value={outcome}>
                {getTranslate(outcome)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            label={getTranslate('Reason')}
            value={draftFilters.reason}
            onChange={(event: { target: { value: unknown } }) =>
              setDraftFilters({
                ...draftFilters,
                reason: event.target.value as FilterForm['reason']
              })
            }>
            <MenuItem value="">{getTranslate('All reasons')}</MenuItem>
            {reasons.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {getTranslate(readable(reason))}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            label={getTranslate('Stage')}
            value={draftFilters.stage}
            onChange={(event: { target: { value: unknown } }) =>
              setDraftFilters({
                ...draftFilters,
                stage: event.target.value as FilterForm['stage']
              })
            }>
            <MenuItem value="">{getTranslate('All stages')}</MenuItem>
            {stages.map((stage) => (
              <MenuItem key={stage} value={stage}>
                {getTranslate(readable(stage))}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <InputWithLabel
            label={getTranslate('Login source')}
            value={draftFilters.loginSource}
            onChange={(event) =>
              setDraftFilters({
                ...draftFilters,
                loginSource: event.currentTarget.value
              })
            }
          />
          <InputWithLabel
            label={getTranslate('From')}
            type="datetime-local"
            value={draftFilters.from}
            onChange={(event) =>
              setDraftFilters({
                ...draftFilters,
                from: event.currentTarget.value
              })
            }
          />
          <InputWithLabel
            label={getTranslate('To')}
            type="datetime-local"
            value={draftFilters.to}
            onChange={(event) =>
              setDraftFilters({
                ...draftFilters,
                to: event.currentTarget.value
              })
            }
          />
        </div>
        <div className={classes.actions}>
          <Button variant="outlined" onClick={resetFilters}>
            {getTranslate('Reset')}
          </Button>
          <Button color="primary" variant="contained" onClick={applyFilters}>
            {getTranslate('Apply Filters')}
          </Button>
        </div>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={32} />
          </div>
        ) : error ? (
          <div className={classes.error}>
            <Typography color="error">
              {getTranslate('Unable to load authentication history')}
            </Typography>
            <Button color="primary" onClick={() => refetch()}>
              {getTranslate('Retry')}
            </Button>
          </div>
        ) : attempts.length ? (
          <>
            <div className={classes.tableWrapper}>
              <Table
                size="small"
                aria-label={getTranslate('Authentication History')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{getTranslate('Occurred')}</TableCell>
                    <TableCell>{getTranslate('Identifier')}</TableCell>
                    <TableCell>{getTranslate('Outcome')}</TableCell>
                    <TableCell>{getTranslate('Reason')}</TableCell>
                    <TableCell>{getTranslate('Stage / Source')}</TableCell>
                    <TableCell>{getTranslate('IP Address')}</TableCell>
                    <TableCell>{getTranslate('Device')}</TableCell>
                    <TableCell>{getTranslate('Entity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((attempt) => (
                    <TableRow key={attempt.id}>
                      <TableCell>
                        {new Date(attempt.occurredAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{attempt.identifier}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getTranslate(attempt.outcome)}
                          className={
                            attempt.outcome === 'Success'
                              ? classes.success
                              : attempt.outcome === 'Failure'
                              ? classes.failure
                              : classes.challenge
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {getTranslate(readable(attempt.reason))}
                      </TableCell>
                      <TableCell>{`${getTranslate(readable(attempt.stage))} / ${
                        attempt.loginSource
                      }`}</TableCell>
                      <TableCell>{attempt.ipAddress || '—'}</TableCell>
                      <TableCell>
                        {[attempt.deviceType, attempt.os, attempt.browser]
                          .filter(Boolean)
                          .join(' / ') || '—'}
                      </TableCell>
                      <TableCell>
                        {attempt.entityCode || attempt.entityId || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className={classes.pagination}>
              <Button
                variant="outlined"
                disabled={!previousTokens.length}
                onClick={() => {
                  const tokens = [...previousTokens]
                  setPageToken(tokens.pop() || '')
                  setPreviousTokens(tokens)
                }}>
                {getTranslate('Previous')}
              </Button>
              <Button
                variant="outlined"
                disabled={!nextPageToken}
                onClick={() => {
                  setPreviousTokens([...previousTokens, pageToken])
                  setPageToken(nextPageToken || '')
                }}>
                {getTranslate('Next')}
              </Button>
            </div>
          </>
        ) : (
          <Typography className={classes.empty}>
            {getTranslate('No authentication attempts match these filters.')}
          </Typography>
        )}
      </section>

      <section className={classes.section}>
        <div className={classes.heading}>
          <Typography variant="h4">
            {getTranslate('History Retention')}
          </Typography>
          <Typography variant="h5" className={classes.subtitle}>
            {getTranslate(
              'New authentication records are retained for this many days. Default: 365 days.'
            )}
          </Typography>
        </div>
        <div className={classes.retentionRow}>
          <InputWithLabel
            label={getTranslate('Retention period (days)')}
            helpertext={getTranslate('Allowed range: 1–3650 days')}
            value={retentionDays}
            className={classes.retentionInput}
            type="text"
            inputMode="numeric"
            onChange={(event) => {
              const value = event.currentTarget.value
              if (!/^\d*$/.test(value)) return
              setRetentionDays(Number(value))
            }}
          />
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              savingRetention || retentionDays < 1 || retentionDays > 3650
            }
            onClick={saveRetention}>
            {getTranslate('Save')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setRetentionDays(
                configurationData?.sessionConfiguration
                  .authenticationHistoryRetentionDays || 365
              )
            }}>
            {getTranslate('Reset')}
          </Button>
        </div>
      </section>
    </div>
  )
}
